import { useEffect } from "react";

/**
 * Lightweight SEO head manager. Sets <title>, meta description, canonical,
 * Open Graph / Twitter tags and an optional JSON-LD block on the document.
 *
 * Kept dependency-free (no react-helmet) so it works inside the existing
 * Vite + React 18 setup without bundle bloat. SSR isn't used, but Googlebot
 * renders JS so client-side updates are picked up.
 */

type SEOProps = {
  title: string; // <60 chars recommended
  description: string; // <160 chars recommended
  /** Path-only or absolute URL. Defaults to current location. */
  canonical?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  /** A single JSON-LD object or array of objects. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
};

const SITE_URL = "https://getpetvet.co.uk";

const setMeta = (
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string,
) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setAlternate = (hreflang: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(
    `link[rel="alternate"][hreflang="${hreflang}"]`,
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SEO = ({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
  noindex,
}: SEOProps) => {
  useEffect(() => {
    document.title = title;

    const canonicalUrl = canonical
      ? canonical.startsWith("http")
        ? canonical
        : `${SITE_URL}${canonical.startsWith("/") ? "" : "/"}${canonical}`
      : `${SITE_URL}${window.location.pathname}`;

    const ogImage = image
      ? image.startsWith("http")
        ? image
        : `${SITE_URL}${image.startsWith("/") ? "" : "/"}${image}`
      : `${SITE_URL}/og-image.jpg`;

    setMeta('meta[name="description"]', "name", "description", description);
    setMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex ? "noindex,nofollow" : "index,follow",
    );
    setLink("canonical", canonicalUrl);

    // hreflang alternates: this app targets UK English only, so publish
    // en-gb and x-default both pointing at the canonical URL. Helps Google
    // pick the right variant in international SERPs and avoids duplicate-
    // content ambiguity when the URL is scraped alongside other locales.
    setAlternate("en-gb", canonicalUrl);
    setAlternate("x-default", canonicalUrl);


    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description,
    );
    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);

    // og:image: when no per-page override, emit all three variants so each
    // platform (Facebook 1.91:1, LinkedIn/WhatsApp 1:1, Pinterest 1:1) picks
    // the best fit. With an override, just set that one.
    document.head
      .querySelectorAll(
        'meta[property="og:image"], meta[property="og:image:width"], meta[property="og:image:height"], meta[property="og:image:type"], meta[property="og:image:alt"]',
      )
      .forEach((el) => el.remove());
    const variants = image
      ? [{ url: ogImage, w: undefined as number | undefined, h: undefined as number | undefined }]
      : [
          { url: `${SITE_URL}/og-image.jpg`, w: 1200, h: 630 },
          { url: `${SITE_URL}/og-image-1200.jpg`, w: 1200, h: 1200 },
          { url: `${SITE_URL}/og-image-square.jpg`, w: 1080, h: 1080 },
        ];
    for (const v of variants) {
      const append = (prop: string, content: string) => {
        const el = document.createElement("meta");
        el.setAttribute("property", prop);
        el.setAttribute("content", content);
        document.head.appendChild(el);
      };
      append("og:image", v.url);
      if (v.w) append("og:image:width", String(v.w));
      if (v.h) append("og:image:height", String(v.h));
      append("og:image:type", "image/jpeg");
      append("og:image:alt", title);
    }

    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description,
    );
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);

    // JSON-LD: replace any prior block written by this component.
    const existing = document.head.querySelector<HTMLScriptElement>(
      'script[type="application/ld+json"][data-seo="page"]',
    );
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "page");
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, canonical, image, type, jsonLd, noindex]);

  return null;
};

export default SEO;
